import { ChangeDetectionStrategy, Component, ElementRef, inject, input } from '@angular/core';
import { ActivitySectionDirective, SpecialsService } from '@core';
import { HeaderComponent, PageHeaderBackSwipeConfig } from '@app/components/header/header.component';
import { SwiperContainerComponent } from '@core/lib/components/swiper/swiper-container.component';
import { SwiperSlideComponent } from '@core/lib/components/swiper/swiper-slide.component';
import { LoadingComponent } from '@app/components/loading/loading.component';
import { combineLatest } from 'rxjs';
import { get } from 'lodash';
import { map } from 'rxjs/operators';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TextComponent } from '@app/components/text/text.component';
import { Location } from '@angular/common';
import { TextTranslatePipe } from '@app/pipes/text-translate.pipe';
import { ImageTranslatePipe } from '@app/pipes/image-translate.pipe';
import { TextTranslatableSchema } from '@app/schemas/text';
import { PageViewSpecialsConditionSchema, PageViewSpecialsSchema } from '@app/views/page-specials/page-specials.schema';
import { SwiperVideoControlDirective } from '@core/lib/components/swiper/swiper-video-control.directive';
import { ImageComponent } from '@app/components/image/image.component';
import { LanguageService } from '@app/services';
import { HeaderBadgeTableNameComponent } from '@app/components/header/header-badge-table-name/header-badge-table-name.component';
import { HeaderBadgeTextComponent } from '@app/components/header/header-badge-text/header-badge-text.component';
import { HeaderTextComponent } from '@app/components/header/header-text/header-text.component';

@Component({
  selector: 'app-page-specials',
  templateUrl: 'page-specials.component.html',
  styleUrl: 'page-specials.component.scss',
  imports: [
    ActivitySectionDirective,
    HeaderComponent,
    SwiperContainerComponent,
    SwiperSlideComponent,
    LoadingComponent,
    TextComponent,
    TextTranslatePipe,
    ImageTranslatePipe,
    SwiperVideoControlDirective,
    ImageComponent,
    HeaderBadgeTableNameComponent,
    HeaderBadgeTextComponent,
    HeaderTextComponent,
  ],
  host: {
    class: 'page',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageSpecialsComponent {

  private readonly location = inject(Location);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly languageService = inject(LanguageService);
  private readonly specialsService = inject(SpecialsService);

  protected readonly backSwipeConfig: PageHeaderBackSwipeConfig = {
    direction: 'vertical',
    element: this.elementRef.nativeElement,
  };

  public readonly pageName = input.required<TextTranslatableSchema>();
  public readonly pageViewConfig = input.required<PageViewSpecialsSchema>();

  protected readonly currentLanguage = toSignal(this.languageService.currentLanguage$, {
    requireSync: true,
  });

  private readonly filter$ = toObservable(this.pageViewConfig).pipe(
    map(({ filter }) => filter),
  );

  private readonly specials$ = combineLatest([
    this.specialsService.specials$,
    this.filter$,
  ]).pipe(
    map(([specials, filter]) => {
      return (
        specials ?? []
      ).filter(special => {
        const today = new Date();
        const start = new Date(special.scheduleStart);
        const end = new Date(special.scheduleEnd);

        return (
          today >= start && today <= end
        );
      }).sort((a, b) => {
        return a.sequence - b.sequence;
      }).filter((special) => {
        if (!filter) {
          return true;
        }

        return this.condition({
          name: special.name,
        }, filter);
      });
    }),
  );

  protected readonly specialsResource = rxResource({
    stream: () => this.specials$,
    defaultValue: [],
  });

  protected onBack() {
    this.location.back();
  }

  private condition(object: object, condition: PageViewSpecialsConditionSchema): boolean {
    if (condition.type === 'group') {
      if (condition.conjunction === 'and') {
        return condition.conditions.every((cond) => this.condition(object, cond));
      }

      return condition.conditions.some((cond) => this.condition(object, cond));
    }

    const fieldValue: unknown = get(object, condition.path);
    const value = condition.value;
    const isStringField = typeof fieldValue === 'string' && typeof value === 'string';

    switch (condition.type) {
      case 'eq':
        return fieldValue === value;
      case '!eq':
        return fieldValue !== value;
      case 'contains':
        return isStringField && fieldValue.includes(value);
      case '!contains':
        return isStringField && !fieldValue.includes(value);
      case 'startsWith':
        return isStringField && fieldValue.startsWith(value);
      case '!startsWith':
        return isStringField && !fieldValue.startsWith(value);
      case 'endsWith':
        return isStringField && fieldValue.endsWith(value);
      case '!endsWith':
        return isStringField && !fieldValue.endsWith(value);
      default:
        return false;
    }
  }

}
